import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
} from 'src/components/ui/sheet';

import { useActiveClient } from 'src/redux/active-client/use-active-client';
import { useGetAccountsQuery, useGetCampaignsQuery } from '../../endpoints/accounts.endpoints';
import { useGetCompaniesQuery } from 'src/redux/billing/billing.endpoints';
import ManageClientDrawerHeader from './manage-client-drawer-header';
import ManageClientPermissions from './manage-client-permissions';
import ManageClientAccess from './manage-client-access';
import ManageClientUsers from './manage-client-users';
import { Button } from 'src/components/ui/button';
import { AlarmClockPlus, Eye, Link, RotateCw } from 'lucide-react';
import ManageClientViewsTemp from './manage-client-views-temp';
import { Client } from '../../types/client.types';
import { useNavigate } from 'react-router-dom';
import ManageClientViews from './manage-client-views';

interface ManageClientDrawerProps {
  open: boolean;
  initialActiveAction?: 'permissions' | 'access' | 'users' | 'views';
  onOpenChange: (open: boolean) => void;
}

export const ManageClientDrawer: React.FC<ManageClientDrawerProps> = ({
  open,
  initialActiveAction = 'permissions',
  onOpenChange,
}) => {
  const { activeClient } = useActiveClient();
  const { data: accounts } = useGetAccountsQuery({});
  const { data: campaigns } = useGetCampaignsQuery({});
  const { data: companies } = useGetCompaniesQuery();

  const [activeAction, setActiveAction] = useState<'permissions' | 'access' | 'users' | 'views'>(initialActiveAction);
  const [activeViewType, setActiveViewType] = useState<'old' | 'new'>('old');
  const [searchValue, setSearchValue] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showInviteUser, setShowInviteUser] = useState(false);

  const navigate = useNavigate();
  // Reset selected IDs when active client changes
  useEffect(() => {
    setSelectedAccountIds([]);
    setSelectedCampaignIds([]);
  }, [activeClient?.ext_client_id]);

  const handleActionSelect = (action: 'permissions' | 'access' | 'users' | 'views') => {
    setSelectedAccountIds([]);
    setSelectedCampaignIds([]);
    setActiveAction(action);
  };

  const handleSelectionChange = (accountIds: string[], campaignIds: string[]) => {
    setSelectedAccountIds(accountIds);
    setSelectedCampaignIds(campaignIds);
  };

  const handlePreview = () => {
    if (!activeClient?.ext_client_id) return;
    navigate(`/reports-old?previewClientId=${activeClient.ext_client_id}&previewClientName=${activeClient.title}`);
  };

  const getActionButton = () => {
    if (activeAction === 'users') {
      return (
        <div className='flex flex-row gap-2'>
          <Button
            variant='outline'
            size='med'
            onClick={() => setShowInviteUser(true)}
          >
            <Link className='w-4 h-4' />
            Invite
          </Button>
          {/* <Button
            variant='outline'
            size='med'
            onClick={() => setShowAddUser(true)}
          >
            <Plus className='w-4 h-4' />
            Add User
          </Button> */}
        </div>

      );
    }

    if (activeAction === 'views') {
      return (
        <div className='flex flex-row gap-2'>
          <Button
            variant='outline'
            size='med'
            onClick={() => setActiveViewType(activeViewType === 'new' ? 'old' : 'new')}
          >
            {activeViewType === 'old' ? <AlarmClockPlus className='w-4 h-4' /> : <RotateCw className='w-4 h-4' />}
            {activeViewType === 'old' ? 'New Dashboard' : 'Past Dashboard'}
          </Button>
          <Button
            variant='outline'
            size='med'
            onClick={handlePreview}
          >
            <Eye className='w-4 h-4' />
            Preview
          </Button>

        </div>
      );
    }

    return null;
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="!w-[75vw] sm:!w-[85vw] !max-w-none bg-white flex flex-col p-0"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <div className='p-6 pt-3 flex flex-col h-full overflow-hidden'>
          <ManageClientDrawerHeader
            activeAction={activeAction}
            activeSelectedAccountIds={selectedAccountIds}
            activeSelectedCampaignIds={selectedCampaignIds}
            onActionSelect={handleActionSelect}
            actionButtonSlot={getActionButton()}
          />

          <div className='flex-1 overflow-y-auto'>
            {activeAction === 'permissions' && (
              <div>
                <ManageClientPermissions
                  permissions={activeClient?.permissions}
                  onOpenChange={onOpenChange}
                />
              </div>
            )}
            {activeAction === 'access' && (
              <div>
                <ManageClientAccess
                  onOpenChange={onOpenChange}
                  onSelectionChange={handleSelectionChange}
                />
              </div>
            )}
            {activeAction === 'users' && (
              <div>
                <ManageClientUsers
                  showAddUser={showAddUser}
                  showInviteUser={showInviteUser}
                  onAddUser={(show) => setShowAddUser(false)}
                  onInviteUser={(show) => setShowInviteUser(false)}
                />
              </div>
            )}
            {activeAction === 'views' && (
              <div className='mt-8'>
                {/* <ReportViewCustomize /> */}
                {activeViewType === 'old' && (
                  <ManageClientViewsTemp
                    client={activeClient ?? {} as Client}
                    isOpen={open}
                    onClose={() => onOpenChange(false)}
                  />
                )}
                {activeViewType === 'new' && (
                  <ManageClientViews />
                )}
              </div>
            )}
          </div>
        </div>

      </SheetContent>
    </Sheet>
  );
};

export default ManageClientDrawer;